import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { createToolbarEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import { getName } from '../../../app/functions.web';
import { getConferenceName, getConferenceTimestamp } from '../../../base/conference/functions';
import { getLocalizedDurationFormatter } from '../../../base/i18n/dateUtil';
import Icon from '../../../base/icons/components/Icon';
import { IconArrowDown, IconArrowUp, IconVolumeUp } from '../../../base/icons/svg';
import { showPiP } from '../../../pip/actions';
import { enterPiP } from '../../../pip/functions';
import { showWarningNotification } from '../../../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../../../notifications/constants';
import { toggleAudioSettings } from '../../../settings/actions.web';
import AudioSettingsPopup from '../../../settings/components/web/audio/AudioSettingsPopup';
import logger from '../../logger';
import MobileMeetingDetailsDrawer from './MobileMeetingDetailsDrawer';
import NarrowHangupButton from './NarrowHangupButton';
/** 小窗按钮图标尺寸。 */
const MOBILE_PIP_ICON_SIZE_PX = 22;
/** 标题旁下拉箭头尺寸。 */
const MOBILE_TITLE_CHEVRON_SIZE_PX = 12;
/** 时长刷新间隔。 */
const MEETING_DURATION_TICK_MS = 1000;
const useStyles = makeStyles()(theme => {
    return {
        root: {
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 8px',
            boxSizing: 'border-box',
            gap: '4px'
        },
        left: {
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            gap: '2px',
            minWidth: '72px'
        },
        center: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            minWidth: 0,
            padding: '0 4px'
        },
        right: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexShrink: 0,
            minWidth: '40px'
        },
        iconButton: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            padding: 0,
            border: 'none',
            borderRadius: '8px',
            background: 'transparent',
            color: theme.palette.text01,
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: theme.palette.ui03
            }
        },
        titleButton: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '2px',
            maxWidth: '100%',
            padding: '2px 6px',
            border: 'none',
            borderRadius: '6px',
            background: 'transparent',
            color: theme.palette.text01,
            cursor: 'pointer',
            ...theme.typography.bodyShortBold,
            '&:hover': {
                backgroundColor: theme.palette.ui03
            }
        },
        titleText: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '160px'
        },
        timer: {
            ...theme.typography.labelRegular,
            color: theme.palette.text03,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.2,
            marginTop: '1px'
        }
    };
});
/**
 * 小窗/退出会议界面图标：对角双向箭头（左上指向右下、右下指向左上，相互对指）。
 * 对应产品要求的「开启小窗口」图标样式。
 *
 * @returns {JSX.Element}
 */
function PipMinimizeIcon() {
    return (React.createElement("svg", { "aria-hidden": 'true', fill: 'none', height: MOBILE_PIP_ICON_SIZE_PX, stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 1.8, viewBox: '0 0 24 24', width: MOBILE_PIP_ICON_SIZE_PX },
        React.createElement("path", { d: 'M6 6 L 11 11' }),
        React.createElement("path", { d: 'M8 11 L 11 11 L 11 8' }),
        React.createElement("path", { d: 'M18 18 L 13 13' }),
        React.createElement("path", { d: 'M16 13 L 13 13 L 13 16' })));
}
/**
 * 手机 / 窄屏会议顶栏：左小窗+扬声器、中标题+下拉会议信息+时长、右结束。
 * 会议详情以底部抽屉展示（对齐主流会议手机端）。
 *
 * @returns {JSX.Element}
 */
const MobileConferenceHeader = () => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [durationText, setDurationText] = useState(getLocalizedDurationFormatter(0));
    const conferenceName = useSelector(getConferenceName);
    const room = useSelector((state) => state['features/base/conference'].room || '');
    const startTimestamp = useSelector(getConferenceTimestamp);
    const appName = (getName() || '').trim() || t('conferenceHeader.appName');
    const pipSupported = typeof document !== 'undefined' && (() => {
        const doc = document;
        if ('pictureInPictureEnabled' in document) {
            return doc.pictureInPictureEnabled !== false;
        }
        return typeof HTMLVideoElement.prototype.requestPictureInPicture === 'function';
    })();
    useEffect(() => {
        if (!startTimestamp) {
            setDurationText(getLocalizedDurationFormatter(0));
            return;
        }
        const tick = () => {
            setDurationText(getLocalizedDurationFormatter(Math.max(0, Date.now() - startTimestamp)));
        };
        tick();
        const timerId = window.setInterval(tick, MEETING_DURATION_TICK_MS);
        return () => window.clearInterval(timerId);
    }, [startTimestamp]);
    const onPipClick = useCallback(() => {
        sendAnalytics(createToolbarEvent('mobile.header.pip'));
        logger.info(`[手机顶栏] ${new Date().toISOString()} 点击小窗，请求系统画中画`);
        const videoElement = document.getElementById('pipVideo');
        if (!videoElement) {
            logger.warn(`[手机顶栏] ${new Date().toISOString()} #pipVideo 不存在，回退 showPiP`);
            dispatch(showPiP());
            if (!document.getElementById('pipVideo')) {
                dispatch(showWarningNotification({
                    titleKey: 'conferenceHeader.pipUnsupported'
                }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
            }
            return;
        }
        if (!pipSupported && typeof videoElement.requestPictureInPicture !== 'function') {
            logger.warn(`[手机顶栏] ${new Date().toISOString()} 当前环境不支持系统小窗`);
            dispatch(showWarningNotification({
                titleKey: 'conferenceHeader.pipUnsupported'
            }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
            return;
        }
        // 必须在点击手势内同步 requestPictureInPicture；勿 await play()。
        void videoElement.play().catch(() => undefined);
        if (!enterPiP(videoElement)) {
            logger.warn(`[手机顶栏] ${new Date().toISOString()} 进入小窗失败，回退 showPiP`);
            dispatch(showPiP());
            if (!videoElement.srcObject) {
                dispatch(showWarningNotification({
                    titleKey: 'conferenceHeader.pipNotReady'
                }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
            }
        }
    }, [dispatch, pipSupported]);
    const onOpenDetails = useCallback(() => {
        sendAnalytics(createToolbarEvent('mobile.header.details', { open: true }));
        logger.info(`[手机顶栏] ${new Date().toISOString()} 打开会议详情抽屉`
            + ` room=${room || '-'} name=${conferenceName || '-'}`);
        setDetailsOpen(true);
    }, [conferenceName, room]);
    const onCloseDetails = useCallback(() => {
        sendAnalytics(createToolbarEvent('mobile.header.details', { open: false }));
        logger.info(`[手机顶栏] ${new Date().toISOString()} 关闭会议详情抽屉`);
        setDetailsOpen(false);
    }, []);
    const onAudioOutputClick = useCallback(() => {
        sendAnalytics(createToolbarEvent('mobile.header.audioOutput'));
        logger.info(`[手机顶栏] ${new Date().toISOString()} 打开扬声器/音频输出设置`);
        dispatch(toggleAudioSettings());
    }, [dispatch]);
    return (React.createElement("div", { className: classes.root },
        React.createElement("div", { className: classes.left },
            React.createElement("button", { "aria-label": t('conferenceHeader.openPip'), className: classes.iconButton, onClick: onPipClick, title: t('conferenceHeader.openPip'), type: 'button' },
                React.createElement(PipMinimizeIcon, null)),
            React.createElement(AudioSettingsPopup, null,
                React.createElement("button", { "aria-label": t('conferenceHeader.audioOutput'), className: classes.iconButton, onClick: onAudioOutputClick, title: t('conferenceHeader.audioOutput'), type: 'button' },
                    React.createElement(Icon, { size: MOBILE_PIP_ICON_SIZE_PX, src: IconVolumeUp })))),
        React.createElement("div", { className: classes.center },
            React.createElement("button", { "aria-expanded": detailsOpen, "aria-label": t('info.meetingInfo'), className: classes.titleButton, onClick: onOpenDetails, type: 'button' },
                React.createElement("span", { className: classes.titleText }, appName),
                React.createElement(Icon, { size: MOBILE_TITLE_CHEVRON_SIZE_PX, src: detailsOpen ? IconArrowUp : IconArrowDown })),
            React.createElement("div", { className: classes.timer }, durationText)),
        React.createElement("div", { className: classes.right },
            React.createElement(NarrowHangupButton, { variant: 'text' })),
        React.createElement(MobileMeetingDetailsDrawer, { durationText: durationText, isOpen: detailsOpen, onClose: onCloseDetails })));
};
export default MobileConferenceHeader;
